import type { ChildTextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { setReactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { TrackChangeType } from 'models/changes'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback } from 'react'
import { v4 as newGuid } from 'uuid'
import { useWorkspace2Store } from '../../../Workspace2Store'

// Used by SelectionOption — registers omWorkspaceInsertUOMHandler when the user
// focuses a Text element inside a pill that already has UOM children.
export const useRegisterUOMHandlerForPillTextFocus = (contentItemId: string | undefined) =>
    useCallback(
        (focusedElId: string, getFocusedElState?: () => { value: string; cursorPos: number }) => {
            setReactiveWorkspaceActions({
                isInsertUOM: true,
                omWorkspaceInsertUOMHandler: () => {
                    const authoredUserId = userInfo()?.currentUserInfo?.kpUserId ?? ''
                    const store = useWorkspace2Store.getState()
                    // Split the focused text at the cursor position captured on onBlur
                    // (fires before the Insert UOM button click). Text before the cursor
                    // goes to the new Text left of the UOM; text after goes to the right.
                    const state = getFocusedElState?.()
                    const fullText = state?.value ?? ''
                    const cursorPos = state?.cursorPos ?? fullText.length
                    const textBefore = fullText.slice(0, cursorPos)
                    const textAfter = fullText.slice(cursorPos)
                    const makeTextEl = (text: string) => ({ id: newGuid(), type: NODE_TYPES.Text, text, elements: [], imperialUnits: '', metricUnits: '', changeType: TrackChangeType.ADDED, placeholderText: '', authoredUserId })
                    const makeUOMEl = () => ({ id: newGuid(), type: 'UnitsOfMeasure', text: '', elements: [], imperialUnits: '', metricUnits: '', changeType: TrackChangeType.ADDED, placeholderText: '', authoredUserId })
                    const newNodes = store.contentItemViewNodes.map((node) => ({
                        ...node,
                        elements: (node.elements || []).map((og) => {
                            if (og.type !== NODE_TYPES.OptionGroup) return og
                            return {
                                ...og,
                                elements: (og.elements || []).map((so) => {
                                    if (so.id !== contentItemId) return so
                                    const soEls: Partial<ChildTextContentElement>[] = so.elements || []
                                    const idx = soEls.findIndex((e) => e.id === focusedElId)
                                    if (idx === -1) return so
                                    return {
                                        ...so,
                                        elements: [
                                            ...soEls.slice(0, idx),
                                            makeTextEl(textBefore),
                                            makeUOMEl(),
                                            makeTextEl(textAfter),
                                            ...soEls.slice(idx + 1),
                                        ],
                                    }
                                }),
                            }
                        }),
                    })) as Partial<ContentItemViewNodesType>[]
                    store.actions.setContentItemViewNodes(newNodes)
                    setReactiveWorkspaceActions({ isInsertUOM: false, omWorkspaceInsertUOMHandler: undefined })
                },
            })
        },
        [contentItemId]
    )
